/**
 * Onboarding tutorial steps for first-time free trial users.
 * Simplified 4-step intro: dashboard → qr → live_queue → dashboard_final.
 * Targets use data-onboarding-id or data-tutorial-id attributes.
 */

export const ONBOARDING_PAGES = ["initial_tour"];

export const getOnboardingStepsForPage = (page) => {
  switch (page) {
    case "initial_tour": {
      return [
        {
          element: '[data-tutorial-id="tutorial-stats"]',
          popover: {
            title: "Welcome",
            description: "Welcome to the Queue admin. This is your main Admin page.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-onboarding-id="onboarding-nav-qr"]',
          popover: {
            title: "QR Code",
            description:
              "Print it out or display it so your customers can scan it.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-tutorial-id="tutorial-live-que"]',
          popover: {
            title: "Live Queue",
            description:
              "Here you can see your customers in the queue and manage them.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-tutorial-id="tutorial-help"]',
          popover: {
            title: "Need help?",
            description:
              "Go back to Admin to manage the queue system, and look for the '?' icon for further guidance.",
            side: "bottom",
            align: "center",
          },
        },
      ];
    }
    // Legacy individual phases (kept for backward compat / page-specific replays)
    case "dashboard": {
      return [
        {
          element: "body",
          popover: {
            title: "Welcome",
            description: "Welcome to the Queue admin. This is your main Admin page.",
            side: "over",
            align: "center",
          },
        },
      ];
    }
    case "qr": {
      return [
        {
          element: '[data-onboarding-id="onboarding-qr-area"]',
          popover: {
            title: "QR Code",
            description:
              "Print it out or display it so your customers can scan it.",
            side: "top",
            align: "center",
          },
        },
      ];
    }
    case "live_queue": {
      return [
        {
          element: '[data-onboarding-id="onboarding-live-queue-panel"]',
          popover: {
            title: "Live Queue",
            description:
              "Here you can see all your customers registered in the queue and manage them.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-onboarding-id="onboarding-add-queue"]',
          popover: {
            title: "Add Queue",
            description:
              "Click 'Add Queue' to add a customer to the queue manually.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-onboarding-id="onboarding-direct-assist"]',
          popover: {
            title: "Direct Assist",
            description:
              "Click 'Direct Assist' if you want to register all the customers who were handled outside the queue or without registering.",
            side: "bottom",
            align: "center",
          },
        },
      ];
    }
    case "dashboard_final": {
      return [
        {
          element: '[data-tutorial-id="tutorial-help"]',
          popover: {
            title: "Need help?",
            description:
              "Go back to Admin to manage the queue system, and look for the '?' icon for further guidance.",
            side: "bottom",
            align: "center",
          },
        },
      ];
    }
    case "service_management": {
      return [
        {
          element: "body",
          popover: {
            title: "Service Management",
            description:
              "Here you manage the services you offer in your queue (e.g. checkout, assistance, returns). Texts & messages are in Settings → Text Editor.",
            side: "over",
            align: "center",
          },
        },
        {
          element: '[data-onboarding-id="onboarding-services-add"]',
          popover: {
            title: "Add New Service",
            description:
              "Create services and give each a name and a short tag. Use drag and drop in the table below to change the order in which they appear to customers.",
            side: "bottom",
            align: "center",
          },
        },
      ];
    }
    case "profile": {
      return [
        {
          element: "body",
          popover: {
            title: "Profile Settings",
            description:
              "This is your profile page. Here you can update your company details, logo, and account settings.",
            side: "over",
            align: "center",
          },
        },
        {
          element: '[data-onboarding-id="onboarding-profile-branch_name"]',
          popover: {
            title: "Company Name/Location Name",
            description:
              "Set your company or location name. This is displayed to your customers.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-onboarding-id="onboarding-profile-save"]',
          popover: {
            title: "Save Changes",
            description:
              "After editing your details, click here to save your changes.",
            side: "top",
            align: "center",
          },
        },
      ];
    }
    case "text_editor": {
      return [
        {
          element: "body",
          popover: {
            title: "Text Editor",
            description:
              "Here you can edit all the text that appears in the customer view.",
            side: "over",
            align: "center",
          },
        },
        {
          element: '[data-onboarding-id="onboarding-text-editor-save"]',
          popover: {
            title: "Save Your Changes",
            description:
              "Remember to save after editing!",
            side: "left",
            align: "center",
          },
        },
        {
          element: '[data-onboarding-id="onboarding-text-editor-preview"]',
          popover: {
            title: "Preview",
            description:
              "Here you can preview the image showing where this text appears.",
            side: "left",
            align: "center",
          },
        },
      ];
    }
    case "menu_management": {
      return [
        {
          element: "body",
          popover: {
            title: "Menu Management",
            description:
              "Here you manage the menu links that appear on your customer-facing pages (e.g. queue screen, thank you page). This short tour highlights the main areas.",
            side: "over",
            align: "center",
          },
        },
        {
          element: '[data-onboarding-id="onboarding-menu-add"]',
          popover: {
            title: "Add Menu Item",
            description:
              "Add new menu items. Each item has a title (label for customers) and a link (URL or path) that customers can tap.",
            side: "bottom",
            align: "center",
          },
        },
        {
          element: '[data-onboarding-id="onboarding-menu-list"]',
          popover: {
            title: "Menu List",
            description:
              "Your menu items appear here. Drag items using the handle to reorder. Use Edit or Delete to change or remove items.",
            side: "top",
            align: "center",
          },
        },
      ];
    }
    default:
      return [];
  }
};
